<template>
	<div class="block sm:hidden max-w-screen">
		<!-- Optional debug bars -->
		<div
			v-if="debug"
			class="fixed left-0 w-full h-0.5 z-50"
			:style="{ top: `${startTriggerPoint}px`, backgroundColor: 'rgba(0,255,0,0.5)' }"
		/>
		<div
			v-if="debug"
			class="fixed left-0 w-full h-0.5 z-50"
			:style="{ top: `${endTriggerPoint}px`, backgroundColor: 'rgba(255,0,0,0.5)' }"
		/>

		<!-- Path container -->
		<div
			ref="pathContainerRef"
			class="absolute top-[40px] -left-4 -x-translate-1/2 ml-[50%] w-[211px] h-[417px] z-[-1]"
		>
			<svg
				width="100%"
				height="100%"
				viewBox="0 0 211 417"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<!-- Paths -->
				<path
					ref="path1Ref"
					d="M307 1H223.758C182.336 1 148.758 34.5786 148.758 76V248.819C148.758 285.326 122.355 316.55 86.3789 322.75V322.75C50.4024 328.95 24 360.174 24 340.681V330"
					stroke="url(#paint0_linear_7003_13477)"
				/>
				<path
					ref="path2Ref"
					d="M310 15H226.758C185.337 15 151.758 48.5786 151.758 90V245.11C151.758 283.229 120.492 313.937 82.379 313.25V313.25C44.2659 312.563 13 343.271 13 300.39V200"
					stroke="url(#paint1_linear_7003_13477)"
					stroke-width="2"
				/>

				<!-- Animated dots -->
				<g
					ref="dot1Ref"
					:class="dotsVisible ? 'opacity-100' : 'opacity-0'"
				>
					<circle
						:cx="dot1Position.x"
						:cy="dot1Position.y"
						r="3"
						fill="#FF5733"
						filter="url(#glow-filter)"
					/>
				</g>
				<g
					ref="dot2Ref"
					:class="dotsVisible ? 'opacity-100' : 'opacity-0'"
				>
					<circle
						:cx="dot2Position.x"
						:cy="dot2Position.y"
						r="3"
						fill="#FF5733"
						filter="url(#glow-filter)"
					/>
				</g>

				<defs>
					<filter
						id="glow-filter"
						x="-300%"
						y="-300%"
						width="600%"
						height="600%"
						filterUnits="objectBoundingBox"
						color-interpolation-filters="sRGB"
					>
						<feFlood
							flood-opacity="0"
							result="BackgroundImageFix"
						/>
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset />
						<feGaussianBlur stdDeviation="5" />
						<feComposite
							in2="hardAlpha"
							operator="out"
						/>
						<feColorMatrix
							type="matrix"
							values="0 0 0 0 1 0 0 0 0 0.341176 0 0 0 0 0.2 0 0 0 1 0"
						/>
						<feBlend
							mode="normal"
							in2="BackgroundImageFix"
							result="effect1_dropShadow"
						/>
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feMorphology
							radius="2"
							operator="dilate"
							in="SourceAlpha"
							result="effect2_dropShadow"
						/>
						<feOffset />
						<feComposite
							in2="hardAlpha"
							operator="out"
						/>
						<feColorMatrix
							type="matrix"
							values="0 0 0 0 1 0 0 0 0 0.341176 0 0 0 0 0.2 0 0 0 0.25 0"
						/>
						<feBlend
							mode="normal"
							in2="effect1_dropShadow"
							result="effect2_dropShadow"
						/>
						<feBlend
							mode="normal"
							in="SourceGraphic"
							in2="effect2_dropShadow"
							result="shape"
						/>
					</filter>
					<linearGradient
						id="paint0_linear_7003_13477"
						x1="226"
						y1="-63.5"
						x2="-1251.4"
						y2="1239.45"
						gradientUnits="userSpaceOnUse"
					>
						<stop
							stop-color="#FF5733"
							stop-opacity="0"
						/>
						<stop
							offset="0.195447"
							stop-color="#FF5733"
						/>
						<stop
							offset="1"
							stop-color="#EE8B2C"
						/>
					</linearGradient>
					<linearGradient
						id="paint1_linear_7003_13477"
						x1="205.5"
						y1="-23.4999"
						x2="-1246.17"
						y2="1277.02"
						gradientUnits="userSpaceOnUse"
					>
						<stop
							stop-color="#FF5733"
							stop-opacity="0"
						/>
						<stop
							offset="0.132543"
							stop-color="#FF5733"
						/>
						<stop
							offset="1"
							stop-color="#EE8B2C"
						/>
					</linearGradient>
				</defs>
			</svg>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useFiberAnimation } from '~/composables/useFiberAnimation'

const props = defineProps({
	debug: {
		type: Boolean,
		default: false
	},
	startTriggerViewportPosition: {
		type: Number,
		default: 0.35
	},
	endTriggerViewportPosition: {
		type: Number,
		default: 0.15
	},
	horizontalSpeed: {
		type: Number,
		default: 3
	},
	verticalSpeed: {
		type: Number,
		default: 1.2
	}
})
const {
	pathContainerRef,
	path1Ref,
	path2Ref,
	dot1Ref,
	dot2Ref,
	dot1Position,
	dot2Position,
	dotsVisible,
	startTriggerPoint,
	endTriggerPoint
} = useFiberAnimation(
	props.startTriggerViewportPosition,
	props.endTriggerViewportPosition,
	props.horizontalSpeed,
	props.verticalSpeed
)
</script>
